import EastIcon from '@mui/icons-material/East';
import WestIcon from '@mui/icons-material/West';
import { CircularProgress, Paper, Typography, Button } from '@mui/material';
import { Stack, Box } from '@mui/system';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';

import FinalReportSection from './finalReportSection';
import ReportInfoSection from './reportInfoSection';

import { FinalReportData } from '../../../../../interfaces/FinalReportData';
import NotFoundPage from '../../../../../Page/public/notFoundPage';
import { reportFetcher } from '../../../../../services/fetchers/finalReport';

interface DetailsPaperProps {
  id: string | undefined;
}

type ItemType = {
  id: number;
  report: string;
  name: string;
  associatedDepartment: string;
  notificationReason: string;
  dateInformed: string;
};

interface ModifiedFinalReportData extends FinalReportData {
  informed_individuals: Array<ItemType>;
  has_informed_individuals: boolean;
}

const DetailsPaper = ({ id }: DetailsPaperProps) => {
  const FINAL_REPORT_URL = '/final-reports';
  const intl = useIntl();
  const [reportExpanded, setReportExpanded] = useState(false);
  const { data, error, isLoading } = useSWR<
  ModifiedFinalReportData,
  AxiosError,
  [string, string | undefined]
  >([FINAL_REPORT_URL, id], ([url, reportID]) => reportFetcher(url, reportID));

  if (isLoading) {
    return (
      <Stack justifyContent='center' alignItems='center' height='100%'>
        <CircularProgress />
      </Stack>
    );
  }

  if (error && !isLoading) {
    return (
      <Stack justifyContent='center' alignItems='center' height='100%'>
        <NotFoundPage
          statusCode={error.response?.status.toString() ?? '400'}
          statusMessage={intl.formatMessage({
            defaultMessage:
              'An unknown error occurred, please try again later.',
          })}
        />
      </Stack>
    );
  }

  if (!data) {
    return (
      <NotFoundPage
        statusCode={'404'}
        statusMessage={intl.formatMessage({
          defaultMessage: 'Final report not found.',
        })}
      />
    );
  }

  const handleToggleExpand = () => {
    setReportExpanded((expandState) => !expandState);
  };

  return (
    <Paper
      sx={{
        borderRadius: '6px',
        border: '1px solid #00000029',
        boxShadow: '0px 0px 4px 0px #0000000A',
        width: '100%',
        backgroundColor: '#FFF',
        height: '960px',
      }}
    >
      <Stack height='100%' direction='column'>
        <Stack
          justifyContent='space-between'
          alignItems='center'
          direction='row'
          sx={{
            borderBottom: '1px solid #00000029',
            padding: '20px',
          }}
        >
          <Box flexGrow={1} flexBasis={0} textAlign='left'>
            <Typography fontSize={16}>{data.template_name}</Typography>
          </Box>
          <Box
            flexGrow={1}
            flexBasis={0}
            textAlign={reportExpanded ? 'left' : 'right'}
          >
            <Button
              variant='text'
              sx={{
                textTransform: 'none',
              }}
              onClick={handleToggleExpand}
              color='darkGray'
              startIcon={reportExpanded ? null : <WestIcon />}
              endIcon={reportExpanded ? <EastIcon /> : null}
            >
              <FormattedMessage defaultMessage='Submitted Report Details' />
            </Button>
          </Box>
        </Stack>
        <Stack
          direction='row'
          justifyContent='space-around'
          spacing={2}
          padding='20px'
          height='90%'
        >
          <FinalReportSection data={data} />
          {reportExpanded && (
            <ReportInfoSection id={data.case_manager_reference_id} />
          )}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default DetailsPaper;
